import _ from 'lodash';
import ListQuestionSchema from '@ax2/common/model/schemas/ListQuestionSchema';
import OptionData from '@ax2/common/model/answers-data/OptionData';
import { CheckboxListQuestionData } from '../components/questions/CheckboxListQuestion';
import GridQuestionSchema from '@ax2/common/model/schemas/GridQuestionSchema';
import { CheckboxGridQuestionData } from '../components/questions/CheckboxGridQuestion';
import AnswerColumnSchema from '@ax2/common/model/schemas/AnswerColumnSchema';
import AnswerRowSchema from '@ax2/common/model/schemas/AnswerRowSchema';
import OptionCellData from '@ax2/common/model/answers-data/OptionCellData';
import {
  CheckboxGridColOption,
  CheckboxGridOption,
  CheckboxGridRowOption,
} from '../components/answers/CheckboxGrid';
import { DropdownMenuQuestionData } from '../components/questions/DropdownMenuQuestion';
import TextQuestionSchema from '@ax2/common/model/schemas/TextQuestionSchema';
import { OpenEndedTextQuestionData } from '../components/questions/OpenEndedTextQuestion';
import TextAnswerData from '@ax2/common/model/answers-data/TextAnswerData';
import SingleOptionAnswerData from '@ax2/common/model/answers-data/SingleOptionAnswerData';
import { RadioButtonGridQuestionData } from '../components/questions/RadioButtonGridQuestion';
import { RadioButtonListQuestionData } from '../components/questions/RadioButtonListQuestion';
import { TextboxGridNumericQuestionData } from '../components/questions/TextboxGridNumericQuestion';
import TextCellData from '@ax2/common/model/answers-data/TextCellData';
import {
  NumericTextFieldGridColOption,
  NumericTextFieldGridOption,
  NumericTextFieldGridRowOption,
} from '../components/answers/NumericTextFieldGrid';
import { getNumberInRangeValidation } from './validationBuilder';
import {
  TextFieldGridColOption,
  TextFieldGridOption,
  TextFieldGridRowOption,
} from '../components/answers/TextFieldGrid';
import { TextboxGridTextQuestionData } from '../components/questions/TextboxGridTextQuestion';
import TextData from '@ax2/common/model/answers-data/TextData';
import { TextboxListNumericQuestionData } from '../components/questions/TextboxListNumericQuestion';
import { TextboxListTextQuestionData } from '../components/questions/TextboxListTextQuestion';
import { OpenEndedNumericQuestionData } from '../components/questions/OpenEndedNumericQuestion';
import {
  RatingGridColOption,
  RatingGridOption,
  RatingGridRowOption,
} from '../components/answers/RatingGrid';
import { RatingGridQuestionData } from '../components/questions/RatingGridQuestion';
import {
  RadioButtonsGridColOption,
  RadioButtonsGridOption,
  RadioButtonsGridRowOption,
} from '../components/answers/RadioButtonsGrid';
import AnswerType from '@ax2/common/model/AnswerType';
import OptionsGridAnswerData from '@ax2/common/model/answers-data/OptionsGridAnswerData';
import UUID from '@ax2/common/model/UUID';
import TextsGridAnswerData from '@ax2/common/model/answers-data/TextsGridAnswerData';
import OptionsListAnswerData from '@ax2/common/model/answers-data/OptionsListAnswerData';
import { TextboxListSumQuestionData } from '../components/questions/TextboxListSumQuestion';
import {
  getTemplateText,
  getTemplateTextIfNotRatingPlaceholder,
} from '@ax2/common/helpers/template';
import { getQuestionInstructions } from '@ax2/common/model/QuestionType';
import { GridRequiredStrategy } from '@ax2/common/model/schemas/GridRequiredOptions';
import { RankingListQuestionData } from '../components/questions/RankingListQuestion';

function baseGridHeaderConversion(
  sData: AnswerColumnSchema[] | AnswerRowSchema[]
) {
  return _.map(sData, opt => {
    return {
      id: opt.id,
      title: getTemplateText(opt.content),
      subtitle: opt.label ? opt.label : undefined,
      isExclusive: opt.typ === AnswerType.NA,
    };
  });
}

function baseRatingsColHeaderConversion(sData: AnswerColumnSchema[]) {
  return _.map(sData, opt => {
    const text = getTemplateTextIfNotRatingPlaceholder(opt.content);
    return {
      id: opt.id,
      title: text ? text : undefined,
      subtitle: opt.label ? opt.label : undefined,
      isExclusive: opt.typ === AnswerType.NA,
    };
  });
}

function baseGridHeaderConversionWithPDefOpt<
  T extends { participantDefinedOption: string | null }
>(
  sData: AnswerColumnSchema[] | AnswerRowSchema[],
  data: T,
  onCompletion: (d: T) => void
) {
  return _.map(sData, opt => {
    if (opt.typ === AnswerType.ParticipantDefined) {
      return getPartisipentDefinedHeaderOpt(opt.id, data, onCompletion);
    } else {
      return {
        id: opt.id,
        title: getTemplateText(opt.content),
        subtitle: opt.label ? opt.label : undefined,
        isExclusive: opt.typ === AnswerType.NA,
      };
    }
  });
}

function getPartisipentDefinedHeaderOpt<
  T extends { participantDefinedOption: string | null }
>(optId: UUID, data: T, onCompletion: (d: T) => void) {
  return {
    id: optId,
    value: data.participantDefinedOption,
    onCompletion: (newParticipantDefinedOption: string | null) => {
      const newData = _.clone(data);
      newData.participantDefinedOption = newParticipantDefinedOption;
      onCompletion(newData);
    },
  };
}

export function getCheckboxListQuestionData(
  schema: ListQuestionSchema,
  isDesktop: boolean,
  number: string | undefined,
  data: OptionsListAnswerData | undefined,
  onCompletion: (data: OptionsListAnswerData) => void
): CheckboxListQuestionData {
  const options = _.map(schema.answerOptions, opt => {
    return opt.typ === AnswerType.ParticipantDefined
      ? {
          id: opt.id,
          value: data ? data.participantDefinedOption : null,
          onCompletion: (newParticipantDefinedOption: string | null) => {
            const newData = new OptionsListAnswerData(
              schema.id,
              data ? data.selected : [],
              newParticipantDefinedOption
            );
            onCompletion(newData);
          },
        }
      : {
          id: opt.id,
          title: getTemplateText(opt.content),
          isExclusive: opt.typ === AnswerType.NA,
        };
  });
  const preselected = _.map(data ? data.selected : [], d => d.id);

  return {
    key: getQuestionKey(schema, data),
    question: getTemplateText(schema.content),
    helperText: getQuestionInstructions(schema, isDesktop),
    number: number,
    options: options,
    preselected: preselected,
    imageUrl: schema.imageUrl,
    onCompletion: newOptions => {
      const newData = _.map(newOptions, n => new OptionData(n));
      onCompletion(
        new OptionsListAnswerData(
          schema.id,
          newData,
          data ? data.participantDefinedOption : null
        )
      );
    },
  };
}
export function getCheckboxGridQuestionData(
  schema: GridQuestionSchema,
  isDesktop: boolean,
  number: string | undefined,
  data: OptionsGridAnswerData | undefined,
  onCompletion: (data: OptionsGridAnswerData) => void
): CheckboxGridQuestionData {
  const cols = baseGridHeaderConversion(schema.columns);
  const rows = baseGridHeaderConversionWithPDefOpt(
    schema.rows,
    data ? data : new OptionsGridAnswerData(schema.id, [], null),
    onCompletion
  );
  const options: CheckboxGridOption[] = _.compact(
    _.map(data ? data.selected : [], d => {
      const col = _.find(cols, c => c.id === d.columnId);
      const row = _.find(rows, r => r.id === d.rowId);
      if (col && row) {
        return {
          colId: col.id,
          rowId: row.id,
          selected: true,
        };
      } else {
        return null;
      }
    })
  );
  const strategy = schema.requiredOptions
    ? schema.requiredOptions.strategy
    : null;
  const isReversedOnMobile =
    strategy &&
    (strategy === GridRequiredStrategy.OnePerRow ||
      strategy === GridRequiredStrategy.AtLeastOnePerRow)
      ? true
      : false;

  return {
    key: getQuestionKey(schema, data),
    question: getTemplateText(schema.content),
    isDesktop: isDesktop,
    helperText: getQuestionInstructions(schema, isDesktop),
    number: number,
    cols: cols as CheckboxGridColOption[],
    rows: rows as CheckboxGridRowOption[],
    preselected: options,
    imageUrl: schema.imageUrl,
    onCompletion: options => {
      const newCellsData = _.map(
        options,
        n => new OptionCellData(n.colId, n.rowId)
      );
      onCompletion(
        new OptionsGridAnswerData(
          schema.id,
          newCellsData,
          data ? data.participantDefinedOption : null
        )
      );
    },
    isReversedOnMobile: isReversedOnMobile,
  };
}

export function getDropdownMenuQuestionData(
  schema: ListQuestionSchema,
  isDesktop: boolean,
  number: string | undefined,
  data: SingleOptionAnswerData | undefined,
  onCompletion: (data: SingleOptionAnswerData | undefined) => void
): DropdownMenuQuestionData {
  const options = _.map(schema.answerOptions, opt => {
    return opt.typ === AnswerType.Standard || opt.typ === AnswerType.NA
      ? {
          id: opt.id,
          title: getTemplateText(opt.content),
        }
      : {
          id: opt.id,
          value: data ? data.participantDefinedOption : null,
          onCompletion: (newParticipantDefinedOption: string | null) => {
            onCompletion(
              new SingleOptionAnswerData(
                schema.id,
                data ? data.selectedId : null,
                newParticipantDefinedOption
              )
            );
          },
        };
  });

  return {
    key: getQuestionKey(schema, data),
    question: getTemplateText(schema.content),
    helperText: getQuestionInstructions(schema, isDesktop),
    number: number,
    options: options,
    preselected: data && data.selectedId ? data.selectedId : undefined,
    imageUrl: schema.imageUrl,
    onCompletion: newOption => {
      onCompletion(
        new SingleOptionAnswerData(
          schema.id,
          newOption,
          data ? data.participantDefinedOption : null
        )
      );
    },
  };
}
export function getOpenEndedTextQuestionData(
  schema: TextQuestionSchema,
  isDesktop: boolean,
  number: string | undefined,
  data: TextAnswerData | undefined,
  onCompletion: (data: TextAnswerData | undefined) => void
): OpenEndedTextQuestionData {
  return {
    key: getQuestionKey(schema, data),
    question: getTemplateText(schema.content),
    helperText: getQuestionInstructions(schema, isDesktop),
    number: number,
    preselected: data && data.text ? data.text : undefined,
    imageUrl: schema.imageUrl,
    onCompletion: newOption => {
      onCompletion(
        newOption ? new TextAnswerData(schema.id, newOption, null) : undefined
      );
    },
  };
}
export function getOpenEndedNumericQuestionData(
  schema: TextQuestionSchema,
  isDesktop: boolean,
  number: string | undefined,
  data: TextAnswerData | undefined,
  onCompletion: (data: TextAnswerData | undefined) => void
): OpenEndedNumericQuestionData {
  const preselected = data && data.text ? parseInt(data.text) : null;

  return {
    key: getQuestionKey(schema, data),
    question: getTemplateText(schema.content),
    helperText: getQuestionInstructions(schema, isDesktop),
    number: number,
    validation: getNumberInRangeValidation(
      0,
      9999999,
      'Selected values should be in range 0 - 9999999'
    ),
    value: preselected,
    imageUrl: schema.imageUrl,
    onCompletion: newOption => {
      onCompletion(
        newOption
          ? new TextAnswerData(schema.id, newOption.toString(), null)
          : undefined
      );
    },
  };
}
export function getRadioButtonGridQuestionData(
  schema: GridQuestionSchema,
  isDesktop: boolean,
  number: string | undefined,
  data: OptionsGridAnswerData | undefined,
  onCompletion: (data: OptionsGridAnswerData) => void
): RadioButtonGridQuestionData {
  const cols = baseGridHeaderConversion(schema.columns);
  const rows = baseGridHeaderConversionWithPDefOpt(
    schema.rows,
    data ? data : new OptionsGridAnswerData(schema.id, [], null),
    onCompletion
  );
  const options: RadioButtonsGridOption[] = _.compact(
    _.map(data ? data.selected : [], d => {
      const col = _.find(cols, c => c.id === d.columnId);
      const row = _.find(rows, r => r.id === d.rowId);
      if (col && row) {
        return {
          colId: col.id,
          rowId: row.id,
          selected: true,
        };
      } else {
        return null;
      }
    })
  );

  return {
    key: getQuestionKey(schema, data),
    question: getTemplateText(schema.content),
    isDesktop: isDesktop,
    helperText: getQuestionInstructions(schema, isDesktop),
    number: number,
    cols: cols as RadioButtonsGridColOption[],
    rows: rows as RadioButtonsGridRowOption[],
    isExclusivePerRow:
      schema.isExclusivePerRow === null ? true : schema.isExclusivePerRow,
    preselected: options,
    imageUrl: schema.imageUrl,
    onCompletion: newOptions => {
      const newCellsData = _.map(
        newOptions,
        n => new OptionCellData(n.colId, n.rowId)
      );
      onCompletion(
        new OptionsGridAnswerData(
          schema.id,
          newCellsData,
          data ? data.participantDefinedOption : null
        )
      );
    },
    questionType: schema.questionType,
  };
}

export function getRadiobuttonListQuestionData(
  schema: ListQuestionSchema,
  isDesktop: boolean,
  number: string | undefined,
  data: SingleOptionAnswerData | undefined,
  onCompletion: (data: SingleOptionAnswerData) => void
): RadioButtonListQuestionData {
  const options = _.map(schema.answerOptions, opt => {
    return opt.typ === AnswerType.Standard || opt.typ === AnswerType.NA
      ? {
          id: opt.id,
          title: getTemplateText(opt.content),
        }
      : {
          id: opt.id,
          value: data ? data.participantDefinedOption : null,
          onCompletion: (newParticipantDefinedOption: string | null) => {
            const newData = new SingleOptionAnswerData(
              schema.id,
              data ? data.selectedId : null,
              newParticipantDefinedOption
            );
            onCompletion(newData);
          },
        };
  });
  const preselected = data
    ? _.find(options, o => o.id === data.selectedId)
    : undefined;

  return {
    key: getQuestionKey(schema, data),
    question: getTemplateText(schema.content),
    helperText: getQuestionInstructions(schema, isDesktop),
    number: number,
    options: options,
    preselected: preselected ? preselected.id : undefined,
    imageUrl: schema.imageUrl,
    onCompletion: newOption => {
      onCompletion(
        new SingleOptionAnswerData(
          schema.id,
          newOption,
          data ? data.participantDefinedOption : null
        )
      );
    },
  };
}

export function getTextboxGridNumericQuestionData(
  schema: GridQuestionSchema,
  isDesktop: boolean,
  number: string | undefined,
  data: TextsGridAnswerData | undefined,
  onCompletion: (data: TextsGridAnswerData) => void
): TextboxGridNumericQuestionData {
  const cols = baseGridHeaderConversion(schema.columns);
  const rows = baseGridHeaderConversionWithPDefOpt(
    schema.rows,
    data ? data : new TextsGridAnswerData(schema.id, [], null),
    onCompletion
  );
  const options: NumericTextFieldGridOption[] = _.compact(
    _.map(data ? data.selected : [], d => {
      const col = _.find(cols, c => c.id === d.columnId);
      const row = _.find(rows, r => r.id === d.rowId);
      const value = d.text ? parseInt(d.text) : null;
      if (col && row) {
        return {
          colId: col.id,
          rowId: row.id,
          value: value,
        };
      } else {
        return null;
      }
    })
  );

  return {
    key: getQuestionKey(schema, data),
    question: getTemplateText(schema.content),
    isDesktop: isDesktop,
    helperText: getQuestionInstructions(schema, isDesktop),
    number: number,
    cols: cols as NumericTextFieldGridColOption[],
    rows: rows as NumericTextFieldGridRowOption[],
    preselected: options,
    imageUrl: schema.imageUrl,
    validation: getNumberInRangeValidation(
      0,
      9999999,
      'Selected values should be in range 0 - 9999999'
    ),
    onCompletion: newOptions => {
      const newCellsData = _.map(
        _.filter(newOptions, n => n.value !== null && n.error === undefined),
        n =>
          new TextCellData(
            n.colId,
            n.rowId,
            n.value ? n.value.toString() : null
          )
      );
      onCompletion(
        new TextsGridAnswerData(
          schema.id,
          newCellsData,
          data ? data.participantDefinedOption : null
        )
      );
    },
  };
}

export function getTextboxGridTextQuestionData(
  schema: GridQuestionSchema,
  number: string | undefined,
  data: TextsGridAnswerData | undefined,
  onCompletion: (data: TextsGridAnswerData) => void
): TextboxGridTextQuestionData {
  const cols = baseGridHeaderConversion(schema.columns);
  const rows = baseGridHeaderConversionWithPDefOpt(
    schema.rows,
    data ? data : new TextsGridAnswerData(schema.id, [], null),
    onCompletion
  );
  const options: TextFieldGridOption[] = _.compact(
    _.map(data ? data.selected : [], d => {
      const col = _.find(cols, c => c.id === d.columnId);
      const row = _.find(rows, r => r.id === d.rowId);
      const value = d.text ? d.text : null;
      if (col && row) {
        return {
          colId: col.id,
          rowId: row.id,
          value: value,
        };
      } else {
        return null;
      }
    })
  );

  return {
    key: getQuestionKey(schema, data),
    question: getTemplateText(schema.content),
    helperText: 'Fill in all fields',
    number: number,
    cols: cols as TextFieldGridColOption[],
    rows: rows as TextFieldGridRowOption[],
    preselected: options,
    imageUrl: schema.imageUrl,
    onCompletion: newOptions => {
      const newCellsData = _.map(
        newOptions,
        n =>
          new TextCellData(
            n.colId,
            n.rowId,
            n.value ? n.value.toString() : null
          )
      );
      onCompletion(
        new TextsGridAnswerData(
          schema.id,
          newCellsData,
          data ? data.participantDefinedOption : null
        )
      );
    },
  };
}

export function getTextboxListNumericQuestionData(
  schema: ListQuestionSchema,
  isDesktop: boolean,
  number: string | undefined,
  data: TextData[] | undefined,
  onCompletion: (data: TextData[]) => void
): TextboxListNumericQuestionData {
  const options = _.map(schema.answerOptions, opt => {
    const value = _.find(data, d => d.id === opt.id);
    return {
      id: opt.id,
      label: getTemplateText(opt.content),
      value: value && value.text ? parseInt(value.text) : null,
    };
  });

  return {
    key: getQuestionKey(schema, data),
    question: getTemplateText(schema.content),
    helperText: getQuestionInstructions(schema, isDesktop),
    number: number,
    options: options,
    validation: getNumberInRangeValidation(
      0,
      9999999,
      'Selected values should be in range 0 - 9999999'
    ),
    isPercentageMode: false,
    imageUrl: schema.imageUrl,
    onCompletion: opts => {
      const values = _.filter(
        opts,
        o => o.value !== null && o.error === undefined
      );
      onCompletion(
        _.map(values, v => {
          return new TextData(
            v.id,
            v.value !== null && v.value !== undefined ? v.value.toString() : ''
          );
        })
      );
    },
  };
}

export function getTextboxListQuestionData(
  schema: ListQuestionSchema,
  isDesktop: boolean,
  number: string | undefined,
  data: TextData[] | undefined,
  onCompletion: (data: TextData[]) => void
): TextboxListTextQuestionData {
  const options = _.map(schema.answerOptions, opt => {
    const value = _.find(data, d => d.id === opt.id);
    return {
      id: opt.id,
      label: opt.label,
      value: value ? value.text : null,
    };
  });

  return {
    key: getQuestionKey(schema, data),
    question: getTemplateText(schema.content),
    helperText: getQuestionInstructions(schema, isDesktop),
    number: number,
    options: options,
    imageUrl: schema.imageUrl,
    onCompletion: opts => {
      const values = _.filter(
        opts,
        o => o.value !== null && o.error === undefined
      );
      onCompletion(
        _.map(values, v => {
          return new TextData(
            v.id,
            v.value !== null && v.value !== undefined ? v.value : ''
          );
        })
      );
    },
  };
}

export function getTextboxListSumQuestionData(
  schema: ListQuestionSchema,
  isDesktop: boolean,
  number: string | undefined,
  data: TextData[] | undefined,
  onCompletion: (data: TextData[]) => void
): TextboxListSumQuestionData {
  const options = _.map(schema.answerOptions, opt => {
    const value = _.find(data, d => d.id === opt.id);
    return {
      id: opt.id,
      label: getTemplateText(opt.content),
      value: value && value.text ? parseInt(value.text) : null,
    };
  });

  return {
    key: getQuestionKey(schema, data),
    question: getTemplateText(schema.content),
    helperText: getQuestionInstructions(schema, isDesktop),
    number: number,
    options: options,
    isPercentageMode: true,
    // TODO: Add validation across all fields to find if sum is exactly 100
    validation: getNumberInRangeValidation(
      0,
      100,
      'Selected values should be in range 0 - 100'
    ),
    imageUrl: schema.imageUrl,
    onCompletion: opts => {
      const values = _.filter(
        opts,
        o => o.value !== null && o.error === undefined
      );
      onCompletion(
        _.map(values, v => {
          return new TextData(
            v.id,
            v.value !== null && v.value !== undefined ? v.value.toString() : ''
          );
        })
      );
    },
  };
}

export function getRatingGridQuestionData(
  schema: GridQuestionSchema,
  isDesktop: boolean,
  number: string | undefined,
  data: OptionCellData[] | undefined,
  onCompletion: (data: OptionCellData[]) => void
): RatingGridQuestionData {
  const cols = baseRatingsColHeaderConversion(schema.columns);
  const rows = baseGridHeaderConversion(schema.rows);
  const options: RatingGridOption[] = _.compact(
    _.map(data, d => {
      const col = _.find(cols, c => c.id === d.columnId);
      const row = _.find(rows, r => r.id === d.rowId);
      if (col && row) {
        return {
          col: col as RatingGridColOption,
          row: row as RatingGridRowOption,
          selected: true,
        };
      } else {
        return null;
      }
    })
  );

  return {
    key: schema.id,
    question: getTemplateText(schema.content),
    isDesktop: isDesktop,
    helperText: getQuestionInstructions(schema, isDesktop),
    number: number,
    cols: cols as RatingGridColOption[],
    rows: rows as RatingGridRowOption[],
    preselected: options,
    imageUrl: schema.imageUrl,
    onCompletion: newOptions => {
      const newData = _.map(
        newOptions,
        n => new OptionCellData(n.col.id, n.row.id)
      );
      onCompletion(newData);
    },
  };
}

export function getRankingListQuestionData(
  schema: ListQuestionSchema,
  isDesktop: boolean,
  number: string | undefined,
  data: OptionData[] | undefined,
  onCompletion: (data: OptionData[]) => void
): RankingListQuestionData {
  const options = _.map(schema.answerOptions, opt => {
    return {
      id: opt.id,
      title: getTemplateText(opt.content),
    };
  });

  return {
    key: getQuestionKey(schema, data),
    question: getTemplateText(schema.content),
    helperText: getQuestionInstructions(schema, isDesktop),
    number: number,
    options: options,
    preselected: _.map(data, d => d),
    imageUrl: schema.imageUrl,
    onCompletion: (newId, i, idx) => {
      let newData: OptionData[] = data || [];
      newData[i] = { id: newId, order: i, index: idx };
      onCompletion(newData);
    },
    onClear: i => {
      let newData: any[] = data || [];
      newData[i] = undefined;
      onCompletion(newData);
    },
  };
}

function getQuestionKey<T extends { id: UUID }, G extends {}>(
  schema: T,
  _data: G | undefined
): string {
  return schema.id; //TODO: rewrite to more smart key later
}
